'use client';
import { useTokensStoreForFunds } from '@/src/hooks/useTokensStoreForFunds';
import { useTokensStoreGeneral } from '@/src/hooks/useTokensStoreGeneral';
import { strToHex } from '@/src/lib/MayzSmartDB';
import {
    EmulatorApi,
    EmulatorEntity,
    fetchWrapper,
    FundWithDetailsEntity,
    JobManagerFrontEnd,
    ProtocolBtnHandlers,
    SiteSettingsApi,
    SiteSettingsEntity,
    Token_With_Metadata
} from '@/src/lib/MayzSmartDB/index';
import { isEmulator } from '@/src/lib/SmartDB/Commons/Constants/constants';
import { pushWarningNotification } from '@/src/lib/SmartDB/Commons/pushNotification';
import { TransactionFrontEndApiCalls } from '@/src/lib/SmartDB/FrontEnd/ApiCalls/Transaction.FrontEnd.Api.Calls';
import { LucidToolsFrontEnd } from '@/src/lib/SmartDB/lib/Lucid/index';
import { useAppStore, useTokensStore, useWalletStore } from '@/src/store/global/index';
import { useRouter } from '@/src/store/router/RouterProvider';
import { TOKEN_GOV_CS, TOKEN_GOV_TN_STR, WEBSITE_TITLE } from '@/src/utils/constants';
import { useEffect, useState } from 'react';
//--------------------------------------
interface AppGeneralProps {
    onLoadComplete?: () => void;
}
export const useAppGeneral = (props: AppGeneralProps) => {
    //--------------------------------------
    const router = useRouter();
    const appStore = useAppStore();
    const walletStore = useWalletStore();
    const tokensStore = useTokensStore();
    //--------------------------------------
    const [showHistoricTask, setShowHistoricTask] = useState(true);
    const [isProcessingTask, setIsProcessingTask] = useState(false);
    const [isFaildedTask, setIsFaildedTask] = useState(false);
    const [isConfirmedTask, setIsConfirmedTask] = useState(false);
    const [processingTaskMessage, setProcessingTaskMessage] = useState('');
    //--------------------------------------
    const [initial1, setinitial1] = useState(new Date().getTime());
    const [initial2, setinitial2] = useState(0);
    //--------------------------------------
    useEffect(() => {
        if (tokensStore.serverTime !== undefined && initial2 === 0) setinitial2(tokensStore.serverTime);
    }, [tokensStore.serverTime]);
    //--------------------------------------
    useEffect(() => {
        if (props.onLoadComplete !== undefined && appStore.swInitApiCompleted === true) {
            props.onLoadComplete();
        }
    }, [appStore.swInitApiCompleted, props.onLoadComplete]);
    //--------------------------------------
    const conditionInitForAppTokens = () => {
        const result = appStore.swInitApiCompleted === true;
        return result;
    };
    //--------------------------------------
    const {
        isTokensStoreReady: isTokensStoreReadyApp,
        isTokensStoreLoading: isTokensStoreLoadingApp,
        tokensFromStore: tokensFromStoreApp,
        setTokensToGet,
    } = useTokensStoreGeneral({
        name: 'AppTokens',
        tokensToGet: [
            { CS: TOKEN_GOV_CS, TN_Hex: strToHex(TOKEN_GOV_TN_STR) },
            { CS: '', TN_Hex: '' },
        ],
        conditionInit: conditionInitForAppTokens,
        dependenciesInit: [appStore.swInitApiCompleted],
        followUp: undefined,
        keepAlive: true,
        swAddPrice: false,
        swAddMetadata: true,
        initPriceUpdater: false,
        swHandleCreateJob: true,
    });
    //--------------------------------------
    const {
        isTokensStoreReady: isTokensStoreReadyParticipation,
        isTokensStoreLoading: isTokensStoreLoadingParticipation,
        tokensFromStore: tokensFromStoreParticipation,
        funds: fundsForGetTokensAndCalculateUserParticipation,
        setFunds: setFundsForGetTokensAndCalculateUserParticipation,
    } = useTokensStoreForFunds({
        mode: 'FundList',
        name: 'FundListForParticipation',
        isLoadedList: true,
        funds: undefined,
        followUp: undefined,
        keepAlive: true,
        addTokenFT: true,
        addSubTokensFT: false,
        swAddPrice: true,
        swAddMetadata: true,
        initPriceUpdater: false,
    });
    //--------------------------------------
    useEffect(() => {
        if (isTokensStoreReadyParticipation === true) {
            //--------------------------------------
            const tokensWithFundName = fundsForGetTokensAndCalculateUserParticipation
                .map((fund) => {
                    //--------------------
                    const tokenFT = tokensStore.getTokenPriceAndMetadata(fund.getNET_id_CS(), fund.fdFundFT_TN_Hex);
                    // const tokenFT = fund.tokenFT
                    //--------------------
                    if (tokenFT !== undefined) {
                        return {
                            ...tokenFT,
                            fundName: fund.name,
                        };
                    }
                })
                .filter((t) => t !== undefined) as Token_With_Metadata[];
            //--------------------------------------
            walletStore.finishLoadingParticipation({ allTokensFTWithFundName: tokensWithFundName });
            //--------------------------------------
            // setFundsForGetTokensAndCalculateUserParticipation([]);
            //--------------------------------------
        }
    }, [isTokensStoreReadyParticipation]);
    //--------------------------------------
    useEffect(() => {
        try {
            const init = async () => {
                //--------------------------------------
                console.log(`[TokensStore] - Prepare - Participation Tokens`);
                //--------------------------------------
                let funds: FundWithDetailsEntity[] = [];
                if (appStore.funds === undefined) {
                    funds = await appStore.loadFunds();
                } else {
                    funds = appStore.funds;
                }
                //--------
                if (funds.length === 0) {
                    walletStore.finishLoadingParticipation({ allTokensFTWithFundName: [] });
                }
                //--------
                setFundsForGetTokensAndCalculateUserParticipation([...funds]);
                //--------
            };
            if (walletStore.swLoadParticipation === true) {
                init();
            }
        } catch (error) {
            console.error(`[TokensStore] - Prepare - Participation - Error: ${error}`);
            walletStore.finishLoadingParticipation({ allTokensFTWithFundName: [] });
            throw error;
        }
    }, [walletStore.swLoadParticipation]);
    //--------------------------------------
    useEffect(() => {
        // MAIN USE EFFECTS TO EXECUTE JOBS OF GETTING TOKEN METADATA, VERY IMPORTANT!!!
        if (tokensStore.jobTokensToAdd.length > 0 && tokensStore.isExecuting === false) {
            tokensStore.executeJobsTokensToAdd();
        }
    }, [tokensStore.jobTokensToAdd, tokensStore.isExecuting]);
    //--------------------------------------
    async function initApi() {
        try {
            //--------------------
            const response = await fetchWrapper(`${process.env.NEXT_PUBLIC_REACT_SERVER_API_URL}/init`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                },
            });
            if (response.status === 200) {
                const datas = await response.json();
                if (!datas.token) {
                    throw `Invalid response format: Challengue Token not found`;
                }
                if (!datas.csrfToken) {
                    throw `Invalid response format: csrf Token not found`;
                }
                //--------------------
                // localStorage.setItem('challengueToken', datas.token);
                // localStorage.setItem('x-csrf-token', datas.csrfToken);
                //--------------------
                window.__CHALLENGE_TOKEN__ = datas.token;
                window.__CSRF_TOKEN__ = datas.csrfToken;
                // window.__NONCE__ = datas.nonce;
                //--------------------
                console.log(`[App] - initApi - response OK - ${datas.status}`);
                //--------------------
            } else {
                const errorData = await response.json();
                //throw `Received status code ${response.status} with message: ${errorData.error.message ? errorData.error.message : errorData.error}`;
                throw `${errorData.error.message ? errorData.error.message : errorData.error}`;
            }
        } catch (error) {
            console.error(`[App] - initApi - Error: ${error}`);
            throw error;
        }
    }
    async function loadSiteSettings(): Promise<void> {
        try {
            if (appStore.siteSettings !== undefined) {
                console.log(`[App] - Site Settings already set`);
            } else {
                //---------------
                console.log(`[App] - Site Settings not set. Preparing...`);
                //---------------
                let siteSettings: SiteSettingsEntity | undefined = undefined;
                //---------------
                siteSettings = await SiteSettingsApi.getOneByParamsApi_<SiteSettingsEntity>({ name: 'Init' });
                //---------------
                if (siteSettings === undefined) {
                    throw `Site Settings Init does not exists`;
                }
                //---------------
                appStore.setSiteSettings(siteSettings);
                //---------------
            }
            //---------------
        } catch (error) {
            console.error(`[App] - loadSiteSettings - Error: ${error}`);
            throw error;
        }
    }
    async function loadEmulatorCurrent(): Promise<EmulatorEntity> {
        try {
            //---------------
            console.log(`[App] - loadEmulator - Preparing Emulator...`);
            //---------------
            let emulatorDB: EmulatorEntity | undefined = undefined;
            //---------------
            emulatorDB = await EmulatorApi.getOneByParamsApi_<EmulatorEntity>({ current: true });
            //---------------
            if (emulatorDB === undefined) {
                throw `Emulator current not found`;
            }
            //---------------
            walletStore.setEmulatorDB(emulatorDB);
            //---------------
            return emulatorDB;
            //---------------
        } catch (error) {
            console.error(`[App] - loadEmulator - Error: ${error}`);
            throw error;
        }
    }

    async function loadProtocol(): Promise<void> {
        try {
            if (appStore.protocol !== undefined) {
                console.log(`[App] - Protocol already set`);
            } else {
                console.log(`[App] - Protocol not set. Loading...`);
                await appStore.loadProtocol();
            }
        } catch (error) {
            console.error(`[App] - loadProtocol - Error: ${error}`);
            throw error;
        }
    }

    async function updateHistoryTask(forceStart?: boolean): Promise<void> {
        try {
            //---------------
            console.log(`[App] - updateHistoryTask - Creating Job ...`);
            //---------------
            try {
                setIsProcessingTask(true);
                setProcessingTaskMessage('');
                setIsFaildedTask(false);
                setIsConfirmedTask(false);
                //---------------
                const success = await ProtocolBtnHandlers.handleBtnUpdateHistory(setProcessingTaskMessage, forceStart);
                //---------------
                if (success) {
                    setIsConfirmedTask(true);
                } else {
                    setIsFaildedTask(true);
                }
                //---------------
            } catch (error) {
                setIsFaildedTask(true);
            } finally {
                setIsProcessingTask(false);
            }
            //---------------
        } catch (error) {
            console.error(`[App] - updateHistoryTask - Error: ${error}`);
            throw error;
        }
    }

    //--------------------------------------
    async function initLucidForUseAsUtils(emulatorDB?: EmulatorEntity) {
        try {
            console.log(`[App] - initLucidForUseAsUtils - Preparing Lucid for use as Utils...`);
            const { lucid } = await LucidToolsFrontEnd.prepareLucidForUseAsUtils(emulatorDB);
            walletStore.setLucidForUseAsUtils(lucid);
        } catch (error) {
            console.error(`[App] - initLucidForUseAsUtils - Error: ${error}`);
            throw error;
        }
    }
    //--------------------------------------
    useEffect(() => {
        const loadProtocolIfNeeded = async () => {
            try {
                //--------------------
                if (appStore.swExistAnyWallet === true && appStore.swExistProtocol === true) {
                    await loadProtocol();
                }
                //--------------------
            } catch (error) {
                console.error(`[App] - init - Error: ${error}`);
                pushWarningNotification(WEBSITE_TITLE, `Error initializing: ${error}`);
            }
        };
        loadProtocolIfNeeded();
    }, [appStore.swExistAnyWallet, appStore.swExistProtocol]);
    //--------------------------------------
    useEffect(() => {
        const initGeneral = async () => {
            try {
                //--------------------
                appStore.setSwInitApiCompleted(false);
                //--------------------
                await initApi();
                //--------------------
                await loadSiteSettings();
                //--------------------
                let emulatorDB: EmulatorEntity | undefined = undefined;
                if (isEmulator) {
                    emulatorDB = await loadEmulatorCurrent();
                }
                //--------------------
                await initLucidForUseAsUtils(emulatorDB);
                //--------------------
                walletStore.getCardanoWallets();
                //--------------------
                const existAnyWallet = await appStore.checkIfExistAnyWallet();
                //--------------------
                const existAnyProtocol = await appStore.checkIfExistProtocol();
                //--------------------
                if (existAnyWallet === true && existAnyProtocol === true) {
                    await loadProtocol();
                }
                //--------------------
                try {
                    await tokensStore.fetchAdaUsdPrice();
                } catch (error) {
                    console.error(`[App] - init - fetchAdaUsdPrice error: ${error}`);
                }
                try {
                   tokensStore.fetchAdaUsdPriceHistoric();
                } catch (error) {
                    console.error(`[App] - init - fetchAdaUsdPriceHistoric error: ${error}`);
                }
                //--------------------
                appStore.setSwInitApiCompleted(true);
                //--------------------
                try {
                    const swCheckAgainTxTimeOut = false;
                    const swCheckAgainTxPendingTimeOut = false;
                    const swCheckAgainTxFailed = false;
                    TransactionFrontEndApiCalls.beginStatusUpdaterJobApi(swCheckAgainTxTimeOut, swCheckAgainTxPendingTimeOut, swCheckAgainTxFailed);
                } catch (error) {
                    console.log(`Error starting Tx Updater Job: ${error}`);
                }
            } catch (error) {
                console.error(`[App] - init - Error: ${error}`);
                pushWarningNotification(WEBSITE_TITLE, `Error initializing: ${error}`);
            }
        };
        initGeneral();
    }, []);
    //--------------------------------------
    return {
        appStore,
        tokensStore,
        initial1,
        setinitial1,
        initial2,
        setinitial2,
        showHistoricTask,
        setShowHistoricTask,
        isProcessingTask,
        setIsProcessingTask,
        isFaildedTask,
        setIsFaildedTask,
        isConfirmedTask,
        setIsConfirmedTask,
        processingTaskMessage,
        setProcessingTaskMessage,
        updateHistoryTask,
        JobManagerFrontEnd,
    };
};
